import {
  Button,
  HorizontalDivider,
  Icon,
  IconButton,
  Typography,
  VerticalDivider
} from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Notification } from '@hero/atoms';
import { useMatchPath } from '@hero/hooks';
import { ROUTE_PATHS } from '@hero/routes';
import { getPagePattern } from '@hero/utils';

import { Step } from './Step';
import * as S from './style';

export const Header = ({
  title,
  step,
  subtitle,
  steps,
  backButton,
  createButton,
  endButton,
  showBorder,
  disableClickToCompleted,
  components = {},
  showSalesNumber,
  style,
  stepNumber,
  badgeChildren
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const salesNumber = t('salesNumber');

  const isReviewAndPay = useMatchPath([
    getPagePattern(ROUTE_PATHS.NEW_RECOGNITION_REVIEW_PAY()),
    getPagePattern(ROUTE_PATHS.NEW_AUTOMATION_REVIEW_ACTIVE())
  ]);

  const handleBackButton = () => {
    if (backButton?.onClick) backButton.onClick();
    else navigate(-1);
  };

  function renderSalesNumber() {
    if (!salesNumber || isReviewAndPay) return null;

    return (
      <>
        <a href={`tel:${salesNumber}`}>
          <S.CallLink variant="link" color="secondary" startIcon={<Icon name="phone" />}>
            {salesNumber}
          </S.CallLink>
        </a>
        {!!endButton?.props && <VerticalDivider $margin="0 8px" $height="24px" />}
      </>
    );
  }

  return (
    <S.Header showBorder={showBorder} hasSubtitle={!!subtitle} style={style}>
      <S.HeaderTop hasSteps={!!steps?.length}>
        <S.PageTitle>
          {backButton && (
            <>
              <IconButton variant="link" color="secondary" onClick={handleBackButton}>
                <Icon name="left-chevron" size="lg" />
              </IconButton>
              <VerticalDivider $margin="0 16px" $height="24px" />
            </>
          )}
          {stepNumber && (
            <S.StepNumber>
              <Typography variant="subtitle2Bold" color="bg">
                {stepNumber}
              </Typography>
            </S.StepNumber>
          )}
          {typeof title === 'string' ? <Typography variant="h3">{title}</Typography> : title}
          {badgeChildren && (
            <S.InfoBadge rounded variant="text" size="sm">
              {badgeChildren}
            </S.InfoBadge>
          )}
          {createButton && (
            <S.ExtraTitle>
              <VerticalDivider $margin="0 16px" $height="24px" />
              <Button
                variant="link"
                onClick={createButton?.onClick}
                startIcon={<Icon name="add" />}
                uppercase
                style={{ textDecoration: 'none', fontWeight: 700 }}
              >
                {createButton?.children}
              </Button>
            </S.ExtraTitle>
          )}
        </S.PageTitle>
        {components.CenterContent && <components.CenterContent />}
        {steps && (
          <S.PageSteps>
            {steps.map((st, index) => (
              <S.StepWrapper key={st.title}>
                {index !== 0 && <HorizontalDivider $margin="0" $width="4rem" />}
                <Step
                  {...st}
                  title={t(st.title)}
                  active={index + 1 === step || (!step && steps.length === index + 1)}
                  completed={index + 1 < step || (!step && steps.length !== index + 1)}
                  disableClickToCompleted={disableClickToCompleted}
                  isFirstStep={index === 0}
                />
              </S.StepWrapper>
            ))}
          </S.PageSteps>
        )}

        <S.EndArea>
          {components.EndContent ? (
            <>
              <components.EndContent />
              {showSalesNumber && renderSalesNumber()}
            </>
          ) : (
            endButton && (
              <>
                {showSalesNumber && renderSalesNumber()}
                <S.EndButton
                  variant="outlined"
                  color="secondary"
                  size="sm"
                  onClick={endButton.onClick}
                  startIcon={endButton.icon}
                  {...endButton.props}
                >
                  {endButton.children}
                </S.EndButton>
              </>
            )
          )}
          {showSalesNumber && renderSalesNumber()}
        </S.EndArea>
      </S.HeaderTop>
      {subtitle && <Notification status="warning" message={subtitle} style={{ marginTop: 16 }} />}
    </S.Header>
  );
};

Header.propTypes = {
  title: PropTypes.string,
  step: PropTypes.number,
  subtitle: PropTypes.string,
  steps: PropTypes.array,
  backButton: PropTypes.shape({ onClick: PropTypes.func }),
  createButton: PropTypes.shape({ onClick: PropTypes.func }),
  endButton: PropTypes.shape({
    props: PropTypes.object,
    onClick: PropTypes.func,
    icon: PropTypes.node,
    children: PropTypes.node
  }),
  showBorder: PropTypes.bool,
  disableClickToCompleted: PropTypes.bool,
  components: PropTypes.shape({
    CenterContent: PropTypes.node,
    EndContent: PropTypes.node
  }),
  showSalesNumber: PropTypes.bool,
  style: PropTypes.object,
  stepNumber: PropTypes.number,
  badgeChildren: PropTypes.node
};
